// OWNER: tracking agent. Team tracking at the top of Reports (SPEC §19, 2026-10-01; TRACKING-SPEC-2026-10-01.md §4.6).
// Admins: Live now (every 60 s), the per-setter scoreboard (Today / Yesterday / This week / Last week / custom) with a team
// total, scoreboard and per-call CSV, a day timeline per setter, and the targets editor. Setters: My scoreboard (today
// and this week against their targets) and their own timeline. Not measured shows as "not measured", never 0.
// Recordings are never linked or shown here.
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { LiveRow, ScoreMetrics, ScoreRow, SetterTargets, TargetsSetting } from '../../../src/shared/api-tracking';
import { TARGET_KEYS } from '../../../src/shared/api-tracking';
import { Empty, ErrorBanner, Modal } from '../components/ui';
import { useMe } from '../me';
import { ApiErrorBox, Field, FixedHead, downloadText, inputValue, useLoad } from './ui';
import { apiErrorView, type ApiErrorView } from './errors';
import { ymdInTz } from './dates';
import { trackingApi } from './tracking-endpoints';
import {
  NOT_MEASURED, TARGET_STATUS_LABEL, TRACK_COLUMNS, TRACK_PRESETS, clock, dialsVsTarget, dispositionMix, flagsOf, fmtHm, minutesSince,
  pctText, scoreboardCsv, stateWord, targetStatus, timelineLine, trackPresetRange, type TrackColumnKey, type TrackPreset,
} from './tracking';
import './tracking.css';

const FOOTNOTE = 'Dial = a call placed. Answered = the line picked up (people, machines, phone menus). Connect = a person answered (any human outcome). '
  + 'DM convo = spoke to the decision-maker; 60 s+ and 3 min+ count connects that long. Talk = minutes on connects; wrap = outcome logged minus hang-up. '
  + 'On dialer = minutes the dial screen was open and visible (or a call was live), captured from 2026-10-01; before that it shows "not measured". '
  + 'Between calls = gaps between one dial ending and the next starting on the same Toronto day (research and hand-picking happen there). '
  + 'Show = held ÷ (held + no-show) for meetings scheduled in the range. Flags: skipped = served by Next and not dialled within 15 min; '
  + 'blocked = stopped by the calling rules; no-dispo = no outcome 15 min after the call; suspect = logged no answer but answered for 30 s+; '
  + 'callbacks late = as of now. Days are Toronto calendar days. Calls are recorded for company records; recordings are not available in the dialer.';

export function TeamTracking() {
  const me = useMe();
  return me.role === 'admin' ? <AdminTracking /> : <SetterTracking />;
}

// ---------- shared cells ----------

const Nm = () => <span class="trk-nm">{NOT_MEASURED}</span>;
const sub = (text: ComponentChildren, title?: string, cls = '') => <span class={`adm-cell-sub ${cls}`} title={title}>{text}</span>;

function rowCells(m: ScoreMetrics, o: { name: ComponentChildren; nameSub: string; targets: SetterTargets | null; single: boolean; open: boolean }): Record<TrackColumnKey, ComponentChildren> {
  const vs = o.targets ? dialsVsTarget(m, o.targets, o.single) : null;
  const st = o.targets ? targetStatus(vs?.value ?? null, o.targets.dialsPerDay, { open: o.open }) : 'no_target';
  const flags = flagsOf(m);
  return {
    setter: <>{o.name}{sub(o.nameSub)}</>,
    dials: <>{m.dials.toLocaleString()}{vs ? sub(vs.text, `Dial target: ${TARGET_STATUS_LABEL[st]}`, st === 'met' ? 'trk-met' : st === 'behind' ? 'trk-behind' : '') : sub('')}</>,
    onDialer: m.onDialerMinutes === null ? <Nm /> : <>{fmtHm(m.onDialerMinutes)}{sub(m.dialsPerHour === null ? '— /h' : `${m.dialsPerHour}/h`, 'dials per hour on the dialer (needs 15 min or more)')}</>,
    firstLast: o.single
      ? (m.firstDialAt ? <span class="mono">{clock(m.firstDialAt)}–{clock(m.lastDialAt)}</span> : '—')
      : <>{m.activeDays} {m.activeDays === 1 ? 'day' : 'days'}{sub('active')}</>,
    between: <>{m.gaps.over20}{sub(m.gaps.longestMin === null ? 'no gaps' : `max ${m.gaps.longestMin} min`, `${m.gaps.over10} gaps of 10 min+; ${m.gaps.totalOver5Min} min in gaps of 5 min+`)}</>,
    connects: <span title={`answered ${m.answered} (${pctText(m.answerRate)})`}>{m.connects.toLocaleString()}{sub(pctText(m.connectRate, 1), 'connect rate')}</span>,
    convos: <>{m.dmConversations}{sub(`${m.conv60} · ${m.conv3m}`, 'connects of 60 s or more · 3 min or more')}</>,
    booked: <>{m.meetingsBooked}{sub(`set ${pctText(m.setRate)}`, 'booked ÷ DM conversations')}</>,
    show: <>{pctText(m.showRate)}{sub(m.meetingsUnlogged ? `${m.meetingsUnlogged} unlogged` : '', 'scheduled over 2 h ago and still marked booked')}</>,
    talk: <>{m.talkMinutes.toFixed(1)}{sub(m.avgWrapSec === null ? 'wrap —' : `wrap ${m.avgWrapSec} s`, 'average wrap-up time')}</>,
    flags: flags.length ? <span class="trk-flags">{flags.map((f) => <span class="chip chip-warn" title={f.title}>{f.label} {f.n}</span>)}</span> : '',
  };
}

function ScoreTable(props: { rows: Array<{ key: string; metrics: ScoreMetrics; cells: Record<TrackColumnKey, ComponentChildren>; onClick?: () => void; total?: boolean }> }) {
  return (
    <div class="adm-table-wrap">
      <table class="adm-fixed">
        <FixedHead cols={TRACK_COLUMNS} />
        <tbody>
          {props.rows.map((r) => (
            <tr class={`${r.total ? 'adm-total' : ''} ${r.onClick ? 'trk-row' : ''}`} onClick={r.onClick} title={r.onClick ? 'Open the day timeline' : undefined}>
              {TRACK_COLUMNS.map((c) => <td class={c.num ? 'num' : undefined}>{r.cells[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- admin ----------

function AdminTracking() {
  const today = ymdInTz(new Date());
  const [preset, setPreset] = useState<TrackPreset | 'custom'>('today');
  const [range, setRange] = useState(trackPresetRange('today', today));
  const valid = !!range.from && !!range.to && range.from <= range.to;
  const board = useLoad(() => (valid ? trackingApi.scoreboard(range) : Promise.resolve(null)), [range.from, range.to]);
  const [timeline, setTimeline] = useState<{ userId: number; name: string; day: string; row: ScoreRow | null } | null>(null);
  const [targets, setTargets] = useState(false);
  const single = range.from === range.to;
  const open = range.to >= today;
  const b = board.data;

  const pick = (p: TrackPreset) => { setPreset(p); setRange(trackPresetRange(p, today)); };
  const openTimeline = (userId: number, name: string, row: ScoreRow | null) => setTimeline({ userId, name, row, day: single ? range.from : (range.to > today ? today : range.to) });

  return (
    <>
      <LivePanel onOpen={(r) => openTimeline(r.userId, r.name, b?.rows.find((x) => x.userId === r.userId) ?? null)} />
      <div class="card stack">
        <div class="adm-toolbar">
          <h3 style="margin:0">Team scoreboard</h3>
          {TRACK_PRESETS.map((p) => <button class={`btn btn-sm ${preset === p.key ? 'btn-ink' : ''}`} onClick={() => pick(p.key)}>{p.label}</button>)}
          <Field label="From"><input type="date" value={range.from} max={range.to} onInput={(e) => { setPreset('custom'); setRange({ ...range, from: inputValue(e) }); }} /></Field>
          <Field label="To"><input type="date" value={range.to} min={range.from} onInput={(e) => { setPreset('custom'); setRange({ ...range, to: inputValue(e) }); }} /></Field>
          <span class="grow" />
          {board.loading ? <span class="adm-sub">Loading…</span> : null}
          <button class="btn btn-sm" onClick={() => setTargets(true)}>Targets…</button>
          <button class="btn btn-sm" disabled={!b} onClick={() => b && downloadText(`valistix-scoreboard-${b.fromYmd}-to-${b.toYmd}.csv`, scoreboardCsv(b))}>Export CSV</button>
          <a class={`btn btn-sm ${valid ? '' : 'disabled'}`} href={valid ? trackingApi.callsCsvUrl(range) : undefined} download title="Every call in the range, one row each (62 days at most)">Calls CSV</a>
        </div>
        {!valid ? <div class="chip chip-bad" style="display:block;padding:8px 10px">Pick a start date on or before the end date.</div> : null}
        <ErrorBanner error={board.error} />
        {b && b.rows.length === 0 ? <Empty>No setters and no dials in this range.</Empty> : null}
        {b && b.rows.length ? (
          <ScoreTable rows={[
            ...b.rows.map((r) => ({
              key: String(r.userId), metrics: r, onClick: () => openTimeline(r.userId, r.name, r),
              cells: rowCells(r, { name: <b>{r.name}</b>, nameSub: r.practice?.avgScore30d !== null && r.practice?.avgScore30d !== undefined ? `practice ${r.practice.avgScore30d}` : (r.role === 'admin' ? 'admin' : 'no practice score'), targets: r.targets, single, open }),
            })),
            ...(b.rows.length > 1 ? [{ key: 'team', total: true, metrics: b.team, cells: rowCells(b.team, { name: 'Team', nameSub: `${b.rows.length} people`, targets: null, single, open }) }] : []),
          ]} />
        ) : null}
        <div class="adm-sub">{FOOTNOTE}</div>
      </div>
      {timeline ? <TimelineModal {...timeline} onClose={() => setTimeline(null)} /> : null}
      {targets ? <TargetsModal people={(b?.rows ?? []).map((r) => ({ id: r.userId, name: r.name }))} onClose={() => setTargets(false)} onSaved={() => void board.reload()} /> : null}
    </>
  );
}

function LivePanel(props: { onOpen: (r: LiveRow) => void }) {
  const live = useLoad(() => trackingApi.live(), []);
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') void live.reload(); }, 60_000);
    return () => clearInterval(t);
  }, [live.reload]);
  const now = new Date();
  const d = live.data;
  return (
    <div class="card stack">
      <div class="adm-toolbar">
        <h3 style="margin:0">Live now</h3>
        <span class="adm-sub">Refreshes every minute.{d ? ` Red = on the dialer, not in a call, nothing dialled or logged for ${d.idleMinutes}+ min.` : ''}</span>
        <span class="grow" />
        {live.loading ? <span class="adm-sub">Loading…</span> : null}
      </div>
      <ErrorBanner error={live.error} />
      {d && d.rows.length === 0 ? <div class="muted">No setters yet.</div> : null}
      {d && d.rows.length ? (
        <div class="trk-live">
          {d.rows.map((r) => {
            const share = r.dialTarget ? Math.min(100, Math.round((r.dialsToday / r.dialTarget) * 100)) : 0;
            return (
              <button class={`trk-chip ${r.idle ? 'trk-idle' : ''}`} onClick={() => props.onOpen(r)} title="Open today's timeline"
                aria-label={`${r.name}: ${stateWord(r.state)}, ${r.dialsToday} dials today${r.idle ? ', idle' : ''}`}>
                <span class="trk-name"><span class={`trk-dot trk-${r.state}`} />{r.name}</span>
                <span class="trk-line">{stateWord(r.state)}{r.campaignName ? ` · ${r.campaignName}` : ''}</span>
                <span class="trk-line">last dial {minutesSince(r.lastDialAt, now)}</span>
                <span class="trk-line">{r.dialsToday}{r.dialTarget !== null ? `/${r.dialTarget}` : ''} dials · {r.connectsToday} connects · {r.meetingsToday} booked</span>
                {r.dialTarget ? <div class="adm-progress" role="progressbar" aria-valuemin={0} aria-valuemax={r.dialTarget} aria-valuenow={r.dialsToday}><div style={`width:${share}%`} /></div> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ---------- timeline ----------

function TimelineModal(props: { userId: number; name: string; day: string; row: ScoreRow | null; onClose: () => void }) {
  const [day, setDay] = useState(props.day);
  const tl = useLoad(() => trackingApi.timeline({ userId: props.userId, day }), [props.userId, day]);
  const mix = props.row ? dispositionMix(props.row) : [];
  return (
    <Modal title={`${props.name}: day timeline`} onClose={props.onClose} width={760}>
      <div class="stack">
        <div class="adm-toolbar">
          <Field label="Day"><input type="date" value={day} onInput={(e) => setDay(inputValue(e))} /></Field>
          <span class="adm-sub">Toronto time. Shaded rows are gaps of 5 min or more between calls.</span>
        </div>
        {mix.length ? <div class="adm-sub">Outcomes in the selected range: {mix.map((m) => `${m.label} ${m.n}`).join(' · ')}</div> : null}
        <ErrorBanner error={tl.error} />
        {tl.loading && !tl.data ? <div class="muted">Loading…</div> : null}
        {tl.data && tl.data.length === 0 ? <Empty>Nothing on this day.</Empty> : null}
        {tl.data && tl.data.length ? (
          <table class="trk-tl">
            <thead><tr><th style="width:70px">Time</th><th style="width:190px">Event</th><th>Detail</th></tr></thead>
            <tbody>{tl.data.map((e) => {
              const l = timelineLine(e);
              return <tr class={e.kind === 'gap' ? 'trk-gap' : e.kind === 'presence' ? 'trk-presence' : ''}><td class="mono">{clock(e.at)}</td><td>{l.what}</td><td>{l.detail}</td></tr>;
            })}</tbody>
          </table>
        ) : null}
      </div>
    </Modal>
  );
}

// ---------- targets ----------

const TARGET_LABEL: Record<keyof SetterTargets, string> = {
  dialsPerDay: 'Dials per day', dmConversationsPerDay: 'DM conversations per day', conversations3mPerDay: '3 min+ conversations per day',
  meetingsPerWeek: 'Meetings per week', talkMinutesPerDay: 'Talk minutes per day', practiceAvgMin: 'Practice average (min score)',
  dialsPerMeetingMax: 'Dials per meeting (max)', showRateMin: 'Show rate (min %)',
};
type TargetForm = Record<keyof SetterTargets, string>;
const toForm = (t: Partial<SetterTargets>): TargetForm => Object.fromEntries(TARGET_KEYS.map((k) => {
  const v = t[k];
  return [k, v === null || v === undefined ? '' : k === 'showRateMin' ? String(Math.round(v * 100)) : String(v)];
})) as TargetForm;
function fromForm(f: TargetForm, partial: boolean): Partial<SetterTargets> {
  const out: Partial<SetterTargets> = {};
  for (const k of TARGET_KEYS) {
    const s = f[k].trim();
    if (s === '') { if (!partial) out[k] = null; continue; }
    const n = Number(s);
    out[k] = k === 'showRateMin' ? Math.round(n) / 100 : n;
  }
  return out;
}

function TargetsModal(props: { people: Array<{ id: number; name: string }>; onClose: () => void; onSaved: () => void }) {
  const loaded = useLoad(() => trackingApi.targets() as Promise<TargetsSetting>, []);
  const [def, setDef] = useState<TargetForm | null>(null);
  const [users, setUsers] = useState<Record<string, TargetForm>>({});
  const [who, setWho] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiErrorView | null>(null);
  useEffect(() => {
    const s = loaded.data;
    if (!s || def) return;
    setDef(toForm(s.default));
    setUsers(Object.fromEntries(Object.entries(s.users).map(([id, t]) => [id, toForm(t)])));
  }, [loaded.data]);
  const save = async () => {
    if (!def) return;
    setBusy(true); setErr(null);
    try {
      const body: TargetsSetting = { default: fromForm(def, false) as SetterTargets, users: {} };
      for (const [id, f] of Object.entries(users)) { const p = fromForm(f, true); if (Object.keys(p).length) body.users[id] = p; }
      await trackingApi.saveTargets(body);
      props.onSaved();
      props.onClose();
    } catch (e) { setErr(apiErrorView(e)); } finally { setBusy(false); }
  };
  const grid = (f: TargetForm, set: (f: TargetForm) => void, placeholder?: TargetForm) => (
    <div class="trk-targets">
      {TARGET_KEYS.map((k) => (
        <Field label={TARGET_LABEL[k]}>
          <input type="number" min={0} max={k === 'showRateMin' || k === 'practiceAvgMin' ? 100 : 1000} step={1} value={f[k]}
            placeholder={placeholder ? (placeholder[k] || 'no target') : 'no target'} onInput={(e) => set({ ...f, [k]: inputValue(e) })} />
        </Field>
      ))}
    </div>
  );
  const blank = toForm({});
  return (
    <Modal title="Setter targets" onClose={props.onClose} width={640}>
      <div class="stack">
        <ErrorBanner error={loaded.error} />
        <div class="adm-sub">Blank = no target. Seeded from the setter playbook §11 (60 dials a day, practice 80, at most 120 dials per meeting, show rate 70%); nothing else is set until you set it.</div>
        {def ? (
          <>
            <h3 style="margin:0">Everyone (default)</h3>
            {grid(def, setDef)}
            <h3 style="margin:8px 0 0">One setter</h3>
            <Field label="Setter">
              <select value={who} onChange={(e) => setWho(inputValue(e))}>
                <option value="">Pick a setter…</option>
                {props.people.map((p) => <option value={String(p.id)}>{p.name}{users[String(p.id)] ? ' (has overrides)' : ''}</option>)}
              </select>
            </Field>
            {who ? (
              <>
                <div class="adm-sub">Blank = use the default shown in grey.</div>
                {grid(users[who] ?? blank, (f) => setUsers({ ...users, [who]: f }), def)}
              </>
            ) : null}
          </>
        ) : null}
        <ApiErrorBox error={err} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose} disabled={busy}>Cancel</button>
          <button class="btn btn-ink" onClick={save} disabled={busy || !def}>{busy ? 'Saving…' : 'Save targets'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- setter ----------

function SetterTracking() {
  const me = useMe();
  const today = ymdInTz(new Date());
  const week = trackPresetRange('this_week', today);
  const day = useLoad(() => trackingApi.scoreboard({ from: today, to: today }), []);
  const wk = useLoad(() => trackingApi.scoreboard(week), []);
  const [timeline, setTimeline] = useState(false);
  const mine = (b: typeof day.data) => b?.rows.find((r) => r.userId === me.id) ?? null;
  const d = mine(day.data), w = mine(wk.data);
  const rows = [
    d ? { key: 'today', metrics: d, cells: rowCells(d, { name: <b>Today</b>, nameSub: d.practice?.avgScore30d != null ? `practice ${d.practice.avgScore30d}` : '', targets: d.targets, single: true, open: true }) } : null,
    w ? { key: 'week', metrics: w, cells: rowCells(w, { name: <b>This week</b>, nameSub: `${week.from} → ${week.to}`, targets: w.targets, single: false, open: true }) } : null,
  ].filter((x): x is NonNullable<typeof x> => x !== null);
  return (
    <div class="card stack">
      <div class="adm-toolbar">
        <h3 style="margin:0">My scoreboard</h3>
        <span class="adm-sub">{d?.targets.dialsPerDay != null ? `Your dial target: ${d.targets.dialsPerDay} a day.` : 'No dial target set.'}</span>
        <span class="grow" />
        <button class="btn btn-sm" onClick={() => setTimeline(true)}>My timeline</button>
      </div>
      <ErrorBanner error={day.error ?? wk.error} />
      {rows.length ? <ScoreTable rows={rows} /> : day.loading || wk.loading ? <div class="muted">Loading…</div> : null}
      <div class="adm-sub">{FOOTNOTE}</div>
      {timeline ? <TimelineModal userId={me.id} name="My day" day={today} row={d} onClose={() => setTimeline(false)} /> : null}
    </div>
  );
}
